import React, { useEffect, useState } from "react";
import { OverlayTrigger, Tooltip } from "react-bootstrap";
import { API_BASE_URL } from "../../../../environment";
import columnNames from "../../../../assets/dictionary/columnNames.json";
import { currencyOptions } from "../../../../components/currency";
import { languageOptions } from "../../../../components/language";
import { timezoneOptions } from "../../../../components/timezone";

interface CreatedBy {
  user_id: number;
  full_name: string;
  email: string;
  phone: string | null;
  avatar_url: string | null;
}

interface ActivityType {
  activity_type_id: number;
  activity_type_key: string;
  activity_type_display_name: string;
}

interface RelatedEntity {
  related_entity_name: string;
  related_entity_id: number;
  title: string;
}

interface Tenant {
  tenant_id: number;
  tenant_name: string;
  domain_name: string;
  time_zone: string;
}

interface Activity {
  activity_id: number;
  activity_type_id: number;
  configuration: string;
  created_datetime: string;
  created_by: CreatedBy;
  tenant_id: number;
  activity_type: ActivityType;
  related_entity?: RelatedEntity;
  tenant: Tenant;
  updated_activity?: Record<string, any>;
}

interface RecentActivitiesProps {
  tenantId: string;
}

const RecentActivities: React.FC<RecentActivitiesProps> = ({ tenantId }) => {
  const [activities, setActivities] = useState<Activity[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const getFieldDisplayName = (fieldKey: string): string => {
    return (columnNames as Record<string, string>)[fieldKey] || fieldKey;
  };

  const getCurrencyLabel = (value: string): string => {
    const currency = currencyOptions.find((o) => o.value === value);
    return currency ? currency.label : value;
  };

  const getLanguageLabel = (value: string): string => {
    const language = languageOptions.find((o) => o.value === value);
    return language ? language.label : value;
  };

  const getTimezoneLabel = (value: string): string => {
    const timezone = timezoneOptions.find((o) => o.value === value);
    return timezone ? timezone.label : value;
  };

  const getDisplayValue = (key: string, value: any): string => {
    const str = String(value);
    if (key.toLowerCase().includes("currency") || key === "currency_code") {
      return getCurrencyLabel(str);
    }
    if (
      key.toLowerCase().includes("language") ||
      key === "preferred_language"
    ) {
      return getLanguageLabel(str);
    }
    if (
      key.toLowerCase().includes("timezone") ||
      key.toLowerCase().includes("time_zone") ||
      key === "time_zone"
    ) {
      return getTimezoneLabel(str);
    }
    return str;
  };

  useEffect(() => {
    const fetchActivities = async () => {
      try {
        setLoading(true);
        setError(null);

        const authHeader = localStorage.getItem("authCred") || "";
        const response = await fetch(
          `${API_BASE_URL}/activity/?tenant_id=${tenantId}&limit=5`,
          {
            headers: {
              Authorization: `Basic ${authHeader}`,
              "Content-Type": "application/json",
            },
          }
        );

        if (!response.ok) {
          throw new Error(`HTTP error! status: ${response.status}`);
        }

        const data = await response.json();
        setActivities(data.results || []);
      } catch (err) {
        console.error("Error fetching activities:", err);
        setError(
          err instanceof Error ? err.message : "Failed to fetch activities"
        );
      } finally {
        setLoading(false);
      }
    };

    if (tenantId) {
      fetchActivities();
    }
  }, [tenantId]);

  const getActivityIcon = (activityType: string) => {
    switch (activityType.toLowerCase()) {
      case "create":
        return { icon: "ti ti-check", color: "bg-success" };
      case "update":
        return { icon: "ti ti-edit", color: "bg-orange" };
      case "delete":
        return { icon: "ti ti-trash", color: "bg-danger" };
      case "note_create":
        return { icon: "ti ti-notes", color: "bg-success" };
      case "note_update":
        return { icon: "ti ti-notes", color: "bg-orange" };
      case "note_delete":
        return { icon: "ti ti-notes", color: "bg-danger" };
      case "file_create":
        return { icon: "fa-regular fa-file", color: "bg-success" };
      case "file_update":
        return { icon: "fa-regular fa-file", color: "bg-orange" };
      case "file_delete":
        return { icon: "fa-regular fa-file", color: "bg-danger" };
      case "task_create":
        return { icon: "fa-solid fa-list-check", color: "bg-success" };
      case "task_update":
        return { icon: "fa-solid fa-list-check", color: "bg-orange" };
      case "task_delete":
        return { icon: "fa-solid fa-list-check", color: "bg-danger" };
      case "lifecycle_product_n_service_create":
        return { icon: "fa-solid fa-cart-shopping", color: "bg-success" };
      case "lifecycle_product_n_service_update":
        return { icon: "fa-solid fa-cart-shopping", color: "bg-orange" };
      case "lifecycle_product_n_service_delete":
        return { icon: "fa-solid fa-cart-shopping", color: "bg-danger" };
      case "email_sent":
        return { icon: "ti ti-mail", color: "bg-info" };
      default:
        return { icon: "ti ti-activity", color: "bg-primary" };
    }
  };

  const getActivityMessage = (
    activityType: string,
    relatedEntityName: string
  ) => {
    const capName =
      relatedEntityName.charAt(0).toUpperCase() + relatedEntityName.slice(1);
    const type = activityType.toUpperCase();

    if (type.includes("NOTE")) {
      if (type.includes("CREATE")) return "created note";
      if (type.includes("UPDATE")) return "updated note";
      if (type.includes("DELETE")) return "deleted note";
    }

    if (type.includes("FILE")) {
      if (type.includes("CREATE")) return "created file";
      if (type.includes("UPDATE")) return "updated file";
      if (type.includes("DELETE")) return "deleted file";
    }

    if (type.includes("TASK")) {
      if (type.includes("CREATE")) return "created task";
      if (type.includes("UPDATE")) return "updated task";
      if (type.includes("DELETE")) return "deleted task";
    }

    if (type.includes("LIFECYCLE_PRODUCT_N_SERVICE")) {
      if (type.includes("CREATE")) return "added Product/Service in ";
      if (type.includes("UPDATE")) return "updated Product/Service in ";
      if (type.includes("DELETE")) return "deleted Product/Service in ";
    }

    if (type === "EMAIL_SENT") {
      return `sent an email to ${capName}`;
    }

    switch (type) {
      case "CREATE":
        return `created ${capName}`;
      case "UPDATE":
        return `updated ${capName}`;
      case "DELETE":
        return `deleted ${capName}`;
      default:
        return `${activityType.toLowerCase()}d ${capName}`;
    }
  };

  const getEntityUrl = (entityName: string, entityId: number): string => {
    const entityType = entityName.toLowerCase();
    switch (entityType) {
      case "company":
        return `/companies/${entityId}`;
      case "contact":
        return `/contacts/${entityId}`;
      case "lead":
        return `/leads/${entityId}`;
      case "pipeline":
        return `/pipelines/${entityId}`;
      case "deal":
        return `/deals/${entityId}`;
      default:
        return "#";
    }
  };

  const groupActivitiesByDate = (items: Activity[]) => {
    const grouped: Record<string, Activity[]> = {};
    items.forEach((act) => {
      const d = new Date(act.created_datetime).toDateString();
      if (!grouped[d]) grouped[d] = [];
      grouped[d].push(act);
    });
    return grouped;
  };

  return (
    <div className="col-md-12 d-flex">
      <div className="card flex-fill">
        <div className="card-header">
          <div className="d-flex align-items-center justify-content-between flex-wrap row-gap-3">
            <h4 className="mb-0 d-flex align-items-center" style={{ fontSize: "1.25rem" }}>
              <span className="db-chart-icon">
                <i className="ti ti-activity" />
              </span>
              Recent Activities
            </h4>
          </div>
        </div>
        {loading ? (
          <div
            className="card-body"
            style={{ height: "300px", overflowY: "auto" }}
          >
            <div className="text-center py-4">
              <div className="spinner-border text-primary" role="status">
                <span className="visually-hidden">Loading...</span>
              </div>
              <div className="mt-2">
                <small className="text-muted">Loading activities...</small>
              </div>
            </div>
          </div>
        ) : error ? (
          <div
            className="card-body"
            style={{ height: "300px", overflowY: "auto" }}
          >
            <div className="alert alert-danger" role="alert">
              <strong>Error:</strong> {error}
            </div>
          </div>
        ) : activities.length === 0 ? (
          <div
            className="card-body"
            style={{ height: "300px", overflowY: "auto" }}
          >
            <div className="text-center" style={{ padding: "60px 20px" }}>
              <i
                className="ti ti-activity text-muted mb-3"
                style={{ fontSize: "3rem" }}
              ></i>
              <h5 className="text-muted">No Activities</h5>
              <p className="text-muted">No recent activities found</p>
            </div>
          </div>
        ) : (
          <div
            className="card-body"
            style={{ height: "300px", overflowY: "auto" }}
          >
            {Object.entries(groupActivitiesByDate(activities)).map(
              ([date, items]) => (
                <div key={date}>
                  <div className="badge badge-soft-purple fs-14 fw-normal shadow-none mb-3">
                    <i className="ti ti-calendar-check me-1" />
                    {new Date(date).toLocaleDateString("en-US", {
                      day: "numeric",
                      month: "long",
                      year: "numeric",
                    })}
                  </div>

                  {items.map((activity) => {
                    const { icon, color } = getActivityIcon(
                      activity.activity_type.activity_type_key
                    );

                    let emailConfig: any = null;
                    if (
                      activity.activity_type.activity_type_key === "EMAIL_SENT" &&
                      activity.configuration
                    ) {
                      try {
                        emailConfig = JSON.parse(activity.configuration);
                      } catch (e) { }
                    }
                    const name =
                      activity.related_entity?.title || "Unknown Entity";
                    const time = new Date(
                      activity.created_datetime
                    ).toLocaleTimeString("en-US", {
                      hour: "2-digit",
                      minute: "2-digit",
                      hour12: true,
                    });

                    return (
                      <div
                        key={activity.activity_id}
                        className="card border shadow-none mb-2"
                      >
                        <div className="card-body p-3">
                          <div className="d-flex">
                            <span
                              className={`avatar avatar-md flex-shrink-0 rounded me-2 ${color}`}
                            >
                              <i className={icon} />
                            </span>
                            <div>
                              <p
                                className="mb-1"
                                style={{ fontSize: "0.75rem" }}
                              >
                                {activity.created_by.full_name}{" "}
                                {getActivityMessage(
                                  activity.activity_type.activity_type_key,
                                  activity.related_entity
                                    ?.related_entity_name || "Unknown Entity"
                                )}{" "}
                                {activity.related_entity && (
                                  <a
                                    href={getEntityUrl(
                                      activity.related_entity
                                        .related_entity_name,
                                      activity.related_entity.related_entity_id
                                    )}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    style={{
                                      textDecoration: "none",
                                      fontWeight: "bold",
                                      color: "#814eeb",
                                    }}
                                  >
                                    {name?.length > 30 ? (
                                      <OverlayTrigger
                                        placement="top"
                                        overlay={<Tooltip id={`tooltip-${activity.activity_id}`}>{name}</Tooltip>}
                                      >
                                        <span>{`"${name.substring(0, 30)}..."`}</span>
                                      </OverlayTrigger>
                                    ) : (
                                      `"${name}"`
                                    )}
                                  </a>
                                )}
                                {emailConfig && emailConfig.mail_message_id && (
                                  <a
                                    href={`/application/email/sent/${emailConfig.mail_message_id}`}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    className="badge bg-primary-transparent text-primary ms-2 p-1 px-2"
                                    style={{
                                      textDecoration: "none",
                                      fontWeight: 500,
                                      verticalAlign: "middle",
                                      display: "inline-flex",
                                      alignItems: "center"
                                    }}
                                  >
                                    View Email <i className="ti ti-external-link ms-1"></i>
                                  </a>
                                )}
                              </p>
                              {activity.activity_type.activity_type_key.toLowerCase() ===
                                "update" &&
                                activity.updated_activity &&
                                Object.keys(activity.updated_activity).length >
                                0 && (
                                  <h6
                                    className="fw-medium mb-1"
                                    style={{ fontSize: "0.75rem" }}
                                  >
                                    {Object.entries(
                                      activity.updated_activity
                                    ).map(([key, value], idx, arr) => {
                                      return (
                                        <span key={key}>
                                          <strong>
                                            {getFieldDisplayName(key)}:{" "}
                                          </strong>
                                          {getDisplayValue(key, value)}
                                          {idx < arr.length - 1 && <br />}
                                        </span>
                                      );
                                    })}
                                  </h6>
                                )}
                              <p style={{ fontSize: "0.75rem" }}>{time}</p>
                            </div>
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>
              )
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default RecentActivities;
